import axiosInstance, { AxiosError } from 'axios';
import qs from 'query-string';

import { HOST_API } from 'src/config-global';
import { CommonResponse, ErrorResponse, SUBSCRIPTION_ERROR_CODES } from 'src/types';
import { getAccessToken } from 'src/utils/token-storage';
import { handle401Error } from 'src/utils/token-refresh';

// ----------------------------------------------------------------------

declare module 'axios' {
  interface AxiosRequestConfig {
    withoutPlatform?: boolean;
  }
}

export const API_ROOT = (HOST_API ?? '').replace(/\/api\/v1\/?$/, '');

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null) => {
  unauthorizedHandler = handler;
};

type SubscriptionBlockedHandler = () => void;

let subscriptionBlockedHandler: SubscriptionBlockedHandler | null = null;

export const setSubscriptionBlockedHandler = (handler: SubscriptionBlockedHandler | null) => {
  subscriptionBlockedHandler = handler;
};

const axios = axiosInstance.create({
  baseURL: HOST_API,
  paramsSerializer: (params) => {
    return qs.stringify(params, {
      arrayFormat: 'comma',
      skipNull: true,
    });
  },
});

axios.interceptors.request.use((config) => {
  const searchParams = new URLSearchParams(window.location.search);
  const platformId = searchParams.get('platform_id') || '';
  const accessToken = getAccessToken();

  if (platformId && !config.withoutPlatform) {
    config.headers['X-Platform-Id'] = platformId;
  }
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<CommonResponse<null>>) => {
    const retryResponse = await handle401Error(error, axios);

    if (retryResponse) return retryResponse;

    if (error.response?.status === 401 && error.config?.headers?.Authorization) {
      unauthorizedHandler?.();
    }

    if (!error.response) {
      return { data: null };
    }

    const errorBody = error.response.data as ErrorResponse | null;

    if (errorBody?.code === SUBSCRIPTION_ERROR_CODES.SUBSCRIPTION_BLOCKED) {
      subscriptionBlockedHandler?.();
    }

    return error.response;
  }
);

export default axios;

// ----------------------------------------------------------------------

export const ENDPOINTS = {
  auth: {
    profile: '/profile',
    profilePassword: '/profile/password',
    login: '/signin',
    register: '/signup',
    forgotPassword: '/forgot-password',
    resetPassword: '/recover-password',
    confirmEmail: '/confirm-email',
    logout: '/logout',
    refresh: '/auth/refresh',
  },
  app: {
    platforms: '/platforms',
    corporateSettings: '/platform/corporate-settings',
    courses: '/courses',
    sections: '/sections',
    lectures: '/lectures',
    categories: '/categories',
    students: '/students',
    studentsImport: '/students/import',
    orgUnits: '/org-units',
    positions: '/positions',
    assignments: '/assignments',
    analytics: {
      studentEngagement: '/analytics/progress',
      progressExport: '/analytics/progress/export',
      points: '/analytics/course-gamification-points',
      pointsExport: '/analytics/course-gamification-points/export',
      widgets: '/analytics/progress/widget',
      memberProgress: '/analytics/progress/members',
      memberProgressExport: '/analytics/progress/members/export',
      contentProgress: '/analytics/progress/content',
      contentProgressExport: '/analytics/progress/content/export',
      contentProgressRoster: '/analytics/progress/content/roster',
      contentProgressRosterExport: '/analytics/progress/content/roster/export',
      trainingControl: '/analytics/training-control',
      trainingControlSummary: '/analytics/training-control/summary',
      trainingControlUnits: '/analytics/training-control/units',
      trainingControlPeople: '/analytics/training-control/people',
      trainingControlExport: '/analytics/training-control/export',
    },
    submissions: '/submissions',
    homework: '/homework',
    contacts: '/contacts',
    exams: '/exams',
    streams: '/streams',
    streamSettings: '/stream-settings',
    groups: '/groups',
    members: '/members',
    roles: '/roles',
    studentGroups: '/student-groups',
    courseTariffs: '/tariffs',
    feedbacks: '/lectures/feedback',
    documents: '/documents',
    examSubmissions: '/exam-submissions',
    practiceResults: '/practice-results',
    courseCollections: '/course-collections',
    auditLogs: '/audit-logs',
    paymentMethods: '/payment-methods',
    salesHistory: '/sales-history',
    salesHistoryWidgets: '/sales-history/widgets',
    subscription: {
      status: '/subscription/status',
      checkout: '/subscription/checkout',
      invoices: '/subscription/invoices',
      payments: '/subscription/payments',
    },
    chatbot: '/chatbot',
    surveys: '/surveys',
    chats: '/chats',
    messengerToken: '/messenger/token',
    messengerSubscriptionToken: '/messenger/subscription-token',
    liveSessions: '/live-sessions',
    liveAudienceOptions: '/live-sessions/audience-options',
    integrations: '/integrations',
    integrationAccounts: '/integrations/accounts',
    mcp: {
      access: '/mcp/access',
      connections: '/mcp/connections',
      settings: '/mcp/settings',
      // Account-level, across every school: no X-Platform-Id.
      myConnections: '/mcp/my-connections',
      myConnectionsRevoke: '/mcp/my-connections/revoke',
    },
  },
  oauth: {
    authorizeRequests: '/oauth/authorize/requests',
  },
  ai: {
    chatCompletions: '/ai/v1/chat/completions',
    // Image generation: one proxy route per provider, each mirroring the provider's own API.
    images: {
      openai: '/ai/v1/images/generations',
      gemini: '/ai/gemini/v1beta/interactions',
      recraft: '/ai/recraft/v1/images/generations',
    },
    chats: '/ai/chats',
  },
  tg: {
    token: '/tg/token',
  },
  common: {
    file: '/file',
    fileCloudflare: '/upload',
    videos: '/videos',
    image: '/image',
  },
  videos: {
    upload: '/videos/upload',
  },
};
